import { parseIssueRange } from '@panelvault/shared';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/controls';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { ApiError } from '../../lib/api';
import { useQuickAdd } from './queries';

interface QuickAddDialogProps {
  open: boolean;
  onClose: () => void;
  seriesId: string;
  knownLocations: string[];
}

/**
 * Bulk entry for a run of issues. The preview uses the same parser as the
 * server, so what the dialog shows is exactly what will be created.
 */
export function QuickAddDialog({ open, onClose, seriesId, knownLocations }: QuickAddDialogProps) {
  const { t } = useTranslation();
  const ids = useId();
  const quickAdd = useQuickAdd();

  const [range, setRange] = useState('');
  const [variantName, setVariantName] = useState('');
  const [addToCollection, setAddToCollection] = useState(true);
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    setRange('');
    setVariantName('');
    setAddToCollection(true);
    setLocation(knownLocations[0] ?? '');
    setError(null);
    setResult(null);
  }, [open, knownLocations]);

  const preview = useMemo(() => parseIssueRange(range), [range]);

  async function submit() {
    if (preview.numbers.length === 0) {
      setError(t('quickAdd.nothingToAdd'));
      return;
    }

    try {
      const response = await quickAdd.mutateAsync({
        seriesId,
        input: {
          range,
          variantName: variantName.trim() || null,
          addToCollection,
          location: location.trim() || null,
        },
      });

      setResult({ created: response.created.length, skipped: response.skipped.length });
      setRange('');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('quickAdd.title')}
      description={t('quickAdd.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={quickAdd.isPending}>
            {t('form.close')}
          </Button>
          <Button onClick={() => void submit()} disabled={quickAdd.isPending}>
            {quickAdd.isPending ? t('form.saving') : t('quickAdd.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('quickAdd.range')} htmlFor={`${ids}-range`} hint={t('quickAdd.rangeHint')}>
          <Input
            id={`${ids}-range`}
            value={range}
            placeholder="1-25, 30, 32"
            onChange={(event) => setRange(event.target.value)}
            autoFocus
          />
        </Field>

        {preview.numbers.length > 0 ? (
          <div className="rounded-lg border bg-surface-raised p-3">
            <p className="text-xs text-muted">
              {t('quickAdd.preview', { count: preview.numbers.length })}
            </p>
            <p className="mt-1 line-clamp-3 font-display text-sm text-foreground">
              {preview.numbers.join(' · ')}
            </p>
          </div>
        ) : null}

        {preview.errors.map((message) => (
          <p key={message} className="text-sm text-danger">
            {message}
          </p>
        ))}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('issue.variant')} htmlFor={`${ids}-variant`}>
            <Input
              id={`${ids}-variant`}
              value={variantName}
              onChange={(event) => setVariantName(event.target.value)}
            />
          </Field>

          <Field label={t('collection.location')} htmlFor={`${ids}-location`}>
            <Input
              id={`${ids}-location`}
              list={`${ids}-locations`}
              value={location}
              onChange={(event) => setLocation(event.target.value)}
            />
            <datalist id={`${ids}-locations`}>
              {knownLocations.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </Field>
        </div>

        <Checkbox
          label={t('quickAdd.addToCollection')}
          checked={addToCollection}
          onChange={(event) => setAddToCollection(event.target.checked)}
        />

        {result ? (
          <p className="text-sm text-success">
            {t('quickAdd.result', { created: result.created, skipped: result.skipped })}
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
